import { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import InterestFreeOption from './InterestFreeOption'
import {
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  getKlarnaBpMaxLimit,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
} from 'lib/payment/paymentUtils'
import {
  KLARNA_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  HOOLAH_BP_PAYMENT_TYPE,
  ATOME_BP_PAYMENT_TYPE,
  AFTER_PAY_BP_PAYMENT_TYPE,
  WAAVE_BP_PAYMENT_TYPE,
} from 'constants/payment'
import { connect } from 'react-redux'
import { getAfterPayBpMaxLimit, isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { getHoolahBpMaxLimit, isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { getAtomeBpMaxLimit, isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import { formatRoundedAmount, formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'

const HowToUseSection = styled.div`
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  max-width: ${rem(680)};
`

const PaymentOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  isOpen?: boolean
  onClose?: () => void
  currency: string
  offer?: App.Offer
}

function InterestFreeLearnMoreModal(props: Props) {
  const { isOpen, onClose, currency, offer } = props
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)

  const payOver4Label = intl.formatMessage({
    id: 'K5WRrOlpnfNVzO9ycgZy',
    defaultMessage: 'Pay over 4 interest-free instalments',
    description: 'Title for interest-free payment option with 4 instalments',
  })

  const payOver3Label = intl.formatMessage({
    id: 'Hnxyn-dnKir9t3leKLj7',
    defaultMessage: 'Pay over 3 interest-free instalments',
    description: 'Title for interest-free payment option with 3 instalments (GBP)',
  })

  const payInThreeLabel = intl.formatMessage({
    id: 'cyhjAsqEuLuv01D2JFBl',
    defaultMessage: 'Pay in three instalments',
    description: 'Title for buy-now-pay-later option with three instalments',
  })

  const payViaDirectDebitLabel = intl.formatMessage({
    id: 'ETxWrAE00DO2YQmSEhks',
    defaultMessage: 'Pay securely via direct debit',
    description: 'Title for direct debit payment option during the checkout and payment process',
  })

  const noMinimumSpendLabel = intl.formatMessage({
    id: 'lF4yVVrmbUL3cHcst_v7',
    defaultMessage: 'No minimum spend.',
    description: 'Content description for payment option with no minimum spend requirement',
  })

  const klarnaPayAlt = intl.formatMessage({
    id: 'a_jEVA9Qap-HR_em4FJs',
    defaultMessage: 'Klarna Pay',
    description: 'Alt text for Klarna payment provider logo in the payment section',
  })
  const afterPayAlt = intl.formatMessage({
    id: 'thEGL02FcoyyTwn1nolf',
    defaultMessage: 'After Pay',
    description: 'Alt text for After Pay payment provider logo in the payment section',
  })
  const shopbackAlt = intl.formatMessage({
    id: '_OA3rfIispl4C0pPpYS_',
    defaultMessage: 'Shopback',
    description: 'Alt text for Shopback payment provider logo in the payment section',
  })
  const atomeAlt = intl.formatMessage({
    id: 'OFr5B0VuBprgejfbGmbz',
    defaultMessage: 'Atome',
    description: 'Alt text for Atome payment provider logo in the payment section',
  })
  const luxPayAlt = intl.formatMessage({
    id: 'KBvawT-3TDBrq2aT06b-',
    defaultMessage: 'LuxPay',
    description: 'Alt text for LuxPay payment provider logo in the payment section',
  })

  const products = [
    {
      id: KLARNA_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt={klarnaPayAlt} height="40" />,
      brand: 'Klarna',
      title: payOver4Label,
      content: intl.formatMessage({
        id: 'rDfAs856966Zxe_tpoNX',
        defaultMessage: 'For purchases under {amount}',
        description: 'Klarna payment option limit description during the checkout and payment process',
      }, { amount: intl.formatNumber(2500, { style: 'currency', currency, maximumFractionDigits: 0 }) }),
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaAvailabilityRange(p.price))),
    },
    {
      id: KLARNA_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt={klarnaPayAlt} height="40" />,
      brand: 'Klarna',
      title: currency === 'GBP' ? payOver3Label : payOver4Label,
      content: intl.formatMessage({
        id: 'VCGxmuXPPpaAnl01eVkg',
        defaultMessage: 'For purchases under {amount}',
        description: 'Klarna buy-now-pay-later payment option limit description',
      }, { amount: formatRoundedAmount(getKlarnaBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaBpAvailabilityRange(p.price, currency))),
    },
    {
      id: AFTER_PAY_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-afterpay')} alt={afterPayAlt} height="40" />,
      brand: 'After pay',
      title: payOver4Label,
      content: intl.formatMessage({
        id: 'WkL-7tjDsjKOKzGjZYnR',
        defaultMessage: 'For purchases under {amount}',
        description: 'Afterpay buy-now-pay-later payment option limit description',
      }, { amount: formatRoundedAmount(getAfterPayBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/afterpay',
      isAvailable: (currency: string) => isAfterPayBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAfterPayBpAvailabilityRange(p.price, currency))),
    },
    {
      id: HOOLAH_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt={shopbackAlt} height="40" />,
      brand: 'Shopback',
      title: payInThreeLabel,
      content: intl.formatMessage({
        id: 'FSq2LPjQTFlhM9VFIXDi',
        defaultMessage: 'For purchases {amount} and under',
        description: 'Shopback pay-later payment option limit description',
      }, { amount: formatRoundedAmountWithDollarType(getHoolahBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/shopbackpaylater',
      isAvailable: (currency: string) => isHoolahBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinHoolahBpAvailabilityRange(p.price, currency))),
    },
    {
      id: ATOME_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt={atomeAlt} height="40" />,
      brand: 'Atome',
      title: payInThreeLabel,
      content: intl.formatMessage({
        id: 'I9kfsjpTTSlCEA_f_Rx_',
        defaultMessage: 'For purchases {amount} and under',
        description: 'Atome pay-later payment option limit description in a modal dialog',
      }, { amount: formatRoundedAmountWithDollarType(getAtomeBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/atome',
      isAvailable: (currency: string) => isAtomeBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAtomeBpAvailabilityRange(p.price, currency))),
    },
    {
      id: WAAVE_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-waave')} alt={luxPayAlt} height="15" />,
      brand: 'LuxPay, powered by Waave',
      title: payViaDirectDebitLabel,
      content: noMinimumSpendLabel,
      to: '/luxpay',
      isAvailable: (currency: string) => isWaaveBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinWaaveBpAvailabilityRange(p.price, currency))),
    },
  ]

  const modalTitle = intl.formatMessage({
    id: 'rpb0XLQ21O09TzHjSafz',
    defaultMessage: 'Interest free payment options',
    description: 'Heading title for the interest-free payment options modal',
  })

  const modalSubtitle = intl.formatMessage({
    id: 'hTsYeNd34RWc236LgyAo',
    defaultMessage: 'Select from the following interest free payment options in checkout.',
    description: 'Subtitle explaining user can select from available interest-free payment options',
  })

  const howToUseHeading = intl.formatMessage({
    id: '5k4zvpvWCpE5bbwULvdF',
    defaultMessage: 'How to use this service',
    description: 'Heading for instructions on using interest-free payment service',
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
    >
      <VerticalSpacer gap={20}>
        <PaymentOptionSection>
          {products.map(product => {
            if (product.isAvailable(currency, offer)) {
              return (<InterestFreeOption
                key={product.id}
                product={product}
              />)
            }
            return null
          })}
        </PaymentOptionSection>
        <HowToUseSection>
          <Heading variant="heading6">{howToUseHeading}</Heading>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'nBo-6gDoaUPotvg-wqTx',
              defaultMessage: 'Select your Escape and checkout as normal. In the checkout select the interest free payment option and continue from there.',
              description: 'Instructions on how to use interest free payment option',
            })}
          </BodyText>
        </HowToUseSection>
      </VerticalSpacer>
    </Modal>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(InterestFreeLearnMoreModal)
